import { Controller } from "@hotwired/stimulus"
import flatpickr from "flatpickr"

// A compact range picker for a From/To input pair nested inside an
// already-open Bootstrap dropdown (PPC pages' "Current Period"/"Compare to"
// sections, app/views/ppc/shared/_date_range_filter.html.erb) -- unlike
// calendar_range_controller.js's own big always-visible inline calendar
// (used by the P&L dashboard/rank tracker, each with exactly ONE range and
// its own standalone dropdown + Apply/Cancel), this has no dropdown/apply
// logic of its own: flatpickr's own popup handles show/hide, and the
// surrounding <form>'s single shared Apply button handles submission for
// whichever values end up in the two named inputs.
//
// `static: true` keeps flatpickr's popup DOM node inside this controller's
// own element (instead of flatpickr's default of appending to <body>), so
// clicking a day cell registers as an "inside" click for Bootstrap's
// dropdown, which uses data-bs-auto-close="outside" -- without this, every
// date pick would immediately close the parent dropdown.
export default class extends Controller {
  static targets = ["fromInput", "toInput"]
  static values = { maxDate: String }

  connect() {
    const [ start, end ] = [ this.fromInputTarget.value, this.toInputTarget.value ]
    this._fp = flatpickr(this.fromInputTarget, {
      mode: "range",
      static: true,
      allowInput: true,
      dateFormat: "Y-m-d",
      maxDate: this.hasMaxDateValue ? this.maxDateValue : null,
      defaultDate: [ this.fromInputTarget.value, this.toInputTarget.value ],
      onChange: (selectedDates) => this.onChange(selectedDates)
    })
    // flatpickr writes the whole range ("a to b") into the From box when it starts; each box should hold its own date.
    this.fromInputTarget.value = start
    this.toInputTarget.value = end
  }

  disconnect() {
    this._fp?.destroy()
  }

  openFromToInput() {
    this._fp.open()
  }

  onChange(selectedDates) {
    if (selectedDates[0]) this.fromInputTarget.value = flatpickr.formatDate(selectedDates[0], "Y-m-d")
    if (selectedDates[1]) this.toInputTarget.value = flatpickr.formatDate(selectedDates[1], "Y-m-d")
  }

  toInputChanged() {
    const start = this.fromInputTarget.value
    const end = this.toInputTarget.value
    if (!start || !end) return

    this._fp.setDate([ start, end ], false)
  }
}
